import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Divider, Grid, Stack, TextField, Typography } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import _ from 'lodash';
import moment from 'moment/moment';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';

import { getAllInventoryProductsNotificationData, getWarehouseProductBatchesNotificationData } from '../../../../core/utils/notification_utils';
import useNotification from '../../../../core/hooks/use_notification';
import { getWarehouseProductBatchesAction, getWarehouseProductBatchesState, invalidateProductBatches } from '../../../../features/inventory/get_warehouse_product_batches';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT } from '../../../../core/constants';
import { Button } from '../../../common/button';
import AutocompleteMultipleSearchOnType from '../../../common/autocomplete';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { getProductsForDocumentAction, getProductsForDocumentState, invalidateProductsForDocument } from '../../../../features/product/get_products_for_document';

const AddProductsDialog = ({
                               foodPartnerId,
                               formik,
                               productsListIndex,
                               dateToCalculateExpirationDate,
                               onlyDelivery,
                               sourceWarehouseId
                           }) => {
    const dispatch = useDispatch();
    const { showErrorMessage } = useNotification();

    const getDocumentProducts = useSelector(!foodPartnerId ? getWarehouseProductBatchesState : getProductsForDocumentState);

    const quantityRef = useRef(null);

    const getLabel = useCallback((product) => {
        const isOnlyDeliveryAndInactive = onlyDelivery && product?.product?.active === false;
        const isNotOnlyDeliveryAndInactive = !onlyDelivery && product?.active === false;

        const isInactive = isOnlyDeliveryAndInactive || isNotOnlyDeliveryAndInactive;
        const inactiveSuffix = isInactive ? ' (Nieaktywny)' : '';

        const name = onlyDelivery
            ? product?.product?.name
            : product?.name;

        return `${name}${inactiveSuffix}`;
    }, [onlyDelivery]);

    const getSecondaryLabel = useCallback((product) => {
        return onlyDelivery && 'Data przydatności: ' + moment(product?.expiryDate, MISC_DATE_ISO_DATE_ONLY_FORMAT).format(MISC_DATE_POLISH_DATE_ONLY_FORMAT) + ', max liczba szt.: ' + product?.quantity;
    }, [onlyDelivery]);

    const mapProductsOptions = useCallback((data) => {
        return data?.map(resource => ({
            key: onlyDelivery ? resource?.product?.id + resource?.productBatchId : resource?.id,
            value: onlyDelivery ? resource?.product?.id + '_' + resource?.productBatchId : resource?.id,
            label: getLabel(resource),
            secondaryLabel: getSecondaryLabel(resource),
            details: {
                name: onlyDelivery ? resource?.product?.name : resource?.name,
                productId: onlyDelivery ? resource?.product?.id : resource?.id,
                productBatchId: onlyDelivery ? resource?.productBatchId : '',
                quantity: '',
                expirationDate: onlyDelivery ? moment(resource?.expiryDate)
                    : (resource?.defaultDaysToExpiration && dateToCalculateExpirationDate)
                        ? moment(dateToCalculateExpirationDate).add(Math.max(resource?.defaultDaysToExpiration - 1, 0), 'days')
                        : '',
                price: onlyDelivery ? '' : (resource?.purchaseNetPrice ? Number(resource.purchaseNetPrice).toFixed(2) : ''),
                type: onlyDelivery ? resource?.product?.type : resource?.type
            },
            original: resource
        }));
    }, [getLabel, getSecondaryLabel, dateToCalculateExpirationDate, onlyDelivery]);

    const [open, setOpen] = useState(false);
    const [productsOptions, setProductsOptions] = useState(mapProductsOptions(getDocumentProducts?.data) || []);
    const [loadingProductsOptions, setLoadingProductsOptions] = useState(false);
    const [chosenProducts, setChosenProducts] = useState([]);

    const onProductsInputChange = (product) => {
        if (product.length >= 3) {
            setProductsOptions([]);
            setLoadingProductsOptions(true);

            getProductsDebounced(product);
        }
    };

    const getProductsDebounced = _.debounce((query) => {
        if (onlyDelivery) {
            dispatch(getWarehouseProductBatchesAction({ sourceWarehouseId, query: { query } }))
                .then(response => {
                    showErrorMessage(getWarehouseProductBatchesNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        const newProductOptions = mapProductsOptions(response?.payload).concat(chosenProducts);
                        const newDistinctProductOptions = [...new Map(newProductOptions.map((option) => [option['value'], option])).values()];
                        setProductsOptions(newDistinctProductOptions);
                    }
                    setLoadingProductsOptions(false);
                });
        } else {
            dispatch(getProductsForDocumentAction({ foodPartnerId, query }))
                .then(response => {
                    showErrorMessage(getAllInventoryProductsNotificationData(response));
                    if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                        const newProductOptions = mapProductsOptions(response?.payload).concat(chosenProducts);
                        const newDistinctProductOptions = [...new Map(newProductOptions.map((option) => [option['value'], option])).values()];
                        setProductsOptions(newDistinctProductOptions);
                    }
                    setLoadingProductsOptions(false);
                });
        }
    }, 600);

    const onClose = () => {
        setOpen(false);
        dispatch(invalidateProductBatches());
        dispatch(invalidateProductsForDocument());
        setChosenProducts([]);
        setProductsOptions([]);
    };

    const onAddProducts = () => {
        if (chosenProducts.length === 0) {
            onClose();
            return;
        }

        const productsList = [...(formik?.values?.productsList[productsListIndex] || [])];
        const productsForValidation = [...(formik?.values?.productsForValidation || [])];

        chosenProducts.forEach(product => {
            const lastItem = productsList[productsList.length - 1];
            const index = onlyDelivery
                ? productsList?.findIndex(p => (p?.productId === product?.original?.product?.id) && (p?.productBatchId === product?.original?.id))
                : productsList?.findIndex(p => p?.productId === product?.original?.id);

            if (index === -1) {
                productsList.push({
                    ...product?.details,
                    position: productsList.length ? lastItem.position + 1 : 0,
                    quantity: parseInt(quantityRef?.current?.value) || 0
                });
            }

            if (onlyDelivery) {
                const validationIndex = productsForValidation?.findIndex(p => (p?.product?.id === product?.original?.product?.id) && (p?.productBatchId === product?.original?.id));

                if (validationIndex === -1) {
                    productsForValidation.push(product?.original);
                }
            }
        });

        if (onlyDelivery) {
            const allProductsLists = [...formik.values.productsList];
            allProductsLists[productsListIndex] = productsList;

            formik.setValues({
                ...formik.values,
                productsList: allProductsLists,
                productsForValidation
            });
        } else {
            formik.setFieldValue(`productsList.${ productsListIndex }`, productsList);
        }

        onClose();
    };

    return (
        <>
            <Box sx={ { width: 'max-content', mt: 1 } } onClick={ () => setOpen(true) }>
                <Stack direction="row" alignItems="center" gap={ 1 }
                       sx={ { cursor: 'pointer' } }>
                    <AddCircleIcon color="primary"/>
                    <Typography fontWeight="bold">Dodaj produkty</Typography>
                </Stack>
            </Box>

            <Dialog PaperProps={ { sx: { maxHeight: 800 } } } open={ open } onClose={ onClose } fullWidth maxWidth="md">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Dodaj produkty</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Grid container spacing={ 3 }>
                        <Grid item xs={ 12 } sm={ 4 }>
                            <TextField
                                name="quantity"
                                label="Liczba sztuk"
                                type="number"
                                fullWidth
                                size="small"
                                inputRef={ quantityRef }
                            />
                        </Grid>
                        <Grid item xs={ 12 }>
                            <AutocompleteMultipleSearchOnType
                                options={ productsOptions }
                                value={ chosenProducts }
                                setValue={ (newValue) => setChosenProducts(newValue) }
                                label="Szukaj produktów (wpisz min. 3 litery)"
                                onInputChange={ (value) => onProductsInputChange(value) }
                                required
                                loading={ loadingProductsOptions }
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <Divider sx={ { mt: 1 } }/>

                <DialogActions sx={ { px: 3, py: 2, gap: 1 } }>
                    <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                    <Button onClick={ onAddProducts }>Dodaj wybrane</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AddProductsDialog;